import React, { useEffect, useState } from 'react';

import { Search, X } from 'lucide-react';
import { queryCache, useMutation, useQuery } from 'react-query';
import { DotBricksLoading, Toast } from 'src/common/components';

import { addCohortMembers, searchCohortUsers } from '../api';
import {
  COHORT_SEARCH_MIN_LENGTH,
  EDITION_AUDIENCE_LABEL,
  MEMBER_ADD_REJECTION_LABEL,
  RECIPIENT_COHORT_MEMBERS_QUERY_KEY,
  RECIPIENT_COHORT_USER_SEARCH_QUERY_KEY,
  RECIPIENT_COHORTS_QUERY_KEY,
} from '../constants';
import { RecipientCohort } from '../types';
import { getCohortErrorMessage } from '../utils';

type AddPersonModalProps = {
  cohort: RecipientCohort;
  onClose: () => void;
};

const AddPersonModal: React.FC<AddPersonModalProps> = ({ cohort, onClose }) => {
  const [query, setQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');

  useEffect(() => {
    const t = setTimeout(() => setDebouncedQuery(query.trim()), 300);
    return () => clearTimeout(t);
  }, [query]);

  const canSearch = debouncedQuery.length >= COHORT_SEARCH_MIN_LENGTH;

  const { data, isLoading } = useQuery(
    [RECIPIENT_COHORT_USER_SEARCH_QUERY_KEY, cohort.id, debouncedQuery],
    () => searchCohortUsers(cohort.id, debouncedQuery),
    { enabled: canSearch, keepPreviousData: true }
  );

  const users = canSearch ? data?.data ?? [] : [];

  const [addMember, { isLoading: isAdding }] = useMutation(
    (userId: number) => addCohortMembers(cohort.id, [userId]),
    {
      onSuccess: (res) => {
        void queryCache.invalidateQueries([RECIPIENT_COHORT_MEMBERS_QUERY_KEY, cohort.id]);
        void queryCache.invalidateQueries([RECIPIENT_COHORT_USER_SEARCH_QUERY_KEY, cohort.id]);
        void queryCache.invalidateQueries(RECIPIENT_COHORTS_QUERY_KEY);
        const [rejection] = res.data.rejected;
        if (rejection) {
          Toast.warning(MEMBER_ADD_REJECTION_LABEL[rejection.reason]);
        } else {
          Toast.success('Persona añadida.');
        }
      },
      onError: (error) => {
        Toast.error(getCohortErrorMessage(error, 'No se pudo añadir a la persona.'));
      },
    }
  );

  return (
    <div className="bg-black/40 fixed inset-0 z-50 flex items-center justify-center">
      <div className="rounded-2xl w-full max-w-lg mx-4 overflow-hidden bg-white shadow-2xl">
        <div className="flex items-center justify-between px-5 py-4 border-b">
          <h3 className="text-base font-bold text-gray-900">Añadir persona a {cohort.name}</h3>
          <button type="button" className="btn btn-ghost p-1 text-gray-400" onClick={onClose}>
            <X size={18} />
          </button>
        </div>

        <div className="px-5 pt-4">
          <div className="relative">
            <Search size={15} className="left-3 top-1/2 absolute text-gray-400 -translate-y-1/2" />
            <input
              className="input pl-9 w-full text-sm"
              placeholder="Buscar por nombre, email o cuenta…"
              value={query}
              autoFocus
              onChange={(e) => setQuery(e.target.value)}
            />
          </div>
        </div>

        <div className="max-h-80 px-5 pb-5 mt-3 space-y-2 overflow-y-auto">
          {canSearch && isLoading && <DotBricksLoading />}
          {!canSearch && (
            <p className="py-6 text-sm text-center text-gray-400">
              Escribe al menos {COHORT_SEARCH_MIN_LENGTH} letras para buscar…
            </p>
          )}
          {canSearch && !isLoading && users.length === 0 && (
            <p className="py-6 text-sm text-center text-gray-400">
              Sin resultados para esa búsqueda.
            </p>
          )}
          {users.map((user) => {
            const blockedReason = user.is_unsubscribed
              ? `Dada de baja en la lista ${EDITION_AUDIENCE_LABEL[cohort.send_list]}`
              : user.is_member && 'Ya es miembro';
            return (
              <div
                key={user.user_id}
                className="rounded-xl flex items-center gap-3 p-3 border border-gray-200"
              >
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-gray-800 truncate">{user.name}</p>
                  <p className="text-xs text-gray-500 truncate">
                    {user.email}
                    {` · ${user.account.name}`}
                  </p>
                  {blockedReason && (
                    <p className="mt-0.5 text-xs text-yellow-700">{blockedReason}</p>
                  )}
                </div>
                <button
                  type="button"
                  className="btn btn-ghost disabled:opacity-50 px-3 py-1 text-xs text-blue-700 border border-gray-200 rounded-full"
                  disabled={!!blockedReason || isAdding}
                  onClick={() => addMember(user.user_id)}
                >
                  Añadir
                </button>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default AddPersonModal;
